import * as Sentry from '@sentry/react';

import { AppEnv, env } from 'utils/env';

import { HttpError } from './error';
import { SessionStorageKey, getSessionStorageItem, hasSessionStorageItem } from './sessionStorage';

// let dialogShownTimeout: any;

const setTag = (event: Sentry.Event, key: string, value: string) => {
  if (!event.tags) {
    event.tags = {};
  }
  event.tags[key] = value;
};

export const registerSentry = () => {
  // initialize error tracking in production
  if (
    (env.APP_ENV === AppEnv.Production || env.APP_ENV === AppEnv.Staging) &&
    window.location.hostname !== 'localhost'
  ) {
    Sentry.init({
      dsn: 'https://c0b499e1dbf54228bed9b33ee32b207b@o422067.ingest.sentry.io/5343700',
      environment: env.APP_ENV,
      normalizeDepth: 10,
      beforeSend(event, hint) {
        if (env.APP_VERSION) {
          setTag(event, 'version', env.APP_VERSION);
        }

        // if fullstory has loaded, track the fullstory session url with the error
        if (window.FS?.getCurrentSessionURL) {
          setTag(event, 'fs_url', window.FS.getCurrentSessionURL(/* withTimestamp */ true));
        }

        if (hasSessionStorageItem(SessionStorageKey.SessionId)) {
          setTag(event, 'session_id', getSessionStorageItem(SessionStorageKey.SessionId) ?? '');
        }

        if (hint?.originalException instanceof HttpError) {
          setTag(event, 'request_id', hint?.originalException.requestId ?? '');
        }

        if (hint && hint.originalException) {
          const err = hint.originalException as Error;
          if (err instanceof HttpError && err.statusCode < 500) {
            // ignore 40X core-api errors, already captured by core-api sentry. In FE they show up as duplicates
            return null;
          }
        }

        return event;
      },
      ignoreErrors: [
        // Fired by antd components and is safe to ignore
        // see https://github.com/ant-design/ant-design/issues/26621
        /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
        // Network related intermittent errors that can be ignored
        /^NetworkError: Connection to Recurrency server disrupted/,
        // Fired by headwayapp trying access main page, but browser denies iframe access
        /^null is not an object \(evaluating 'this.iframe.contentWindow'\)/,
        // antd render error, that we have little control over. Not critical
        /^Error: Unable to find node on an unmounted component./,
        // antd scroll observers issue that we have little control over.
        /\bgetBoundingClientRect\b/,
        /^TypeError: M is null$/,
        // 3rd party js widgets get security issues due to cross-frame access
        /^Blocked a frame with origin/,
        // auth0 state/token expired, browser does auto-redirect login
        /^Invalid state/,
        /^Error: Login required$/,
      ],
    });
  }
};
